import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useTenant } from "../contexts/TenantContext";
import {
  getEmailTemplateRegistry,
  getTenantConfig,
  updateTenantConfig,
  sendTestEmailTemplate,
} from "../api/apiClient";
import EmailTemplateEditorDialog, {
  TEMPLATE_DEFS,
} from "../components/EmailTemplateEditorDialog";

const DS = {
  ink: "var(--color-ink)", ink2: "var(--color-ink-2)", ink3: "var(--color-ink-3)", ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)", line2: "var(--color-surface-press-light)",
  surface2: "var(--color-surface-2)",
  shadowSm: "0 1px 0 rgba(20,24,40,.04), 0 1px 3px rgba(20,24,40,.06)",
  danger: "#B42318",
};

const CLASSIFICATION_COLORS = {
  essential: { bg: "rgba(21,128,61,0.10)", color: "#15803d" },
  operational: { bg: "rgba(106,95,193,0.12)", color: "#6a5fc1" },
  optional: { bg: "var(--color-surface-press-light)", color: DS.ink3 },
};

function humanize(templateId) {
  return templateId
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function groupByFamily(entries) {
  const groups = new Map();
  for (const entry of entries) {
    const key = entry.family || "Other";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(entry);
  }
  return groups;
}

export default function EmailLibraryPage() {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const { activeTenantId, user } = useTenant();

  const [entries, setEntries] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editorTemplateId, setEditorTemplateId] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);

  useEffect(() => {
    if (!activeTenantId) return;
    setLoading(true);
    setLoadError(null);
    Promise.all([
      getEmailTemplateRegistry(activeTenantId),
      getTenantConfig(activeTenantId),
    ])
      .then(([registryEntries, config]) => {
        setEntries(registryEntries);
        setEmailTemplates(config?.trainingCenter?.emailTemplates ?? {});
      })
      .catch(() => setLoadError("Could not load the email template library."))
      .finally(() => setLoading(false));
  }, [activeTenantId]);

  const grouped = useMemo(() => groupByFamily(entries), [entries]);

  const editorEntry = entries.find((e) => e.templateId === editorTemplateId);

  const handleOpenEditor = (templateId) => {
    setEditorTemplateId(templateId);
    setEditorOpen(true);
  };

  const handleSaveTemplate = async (templateId, config) => {
    await updateTenantConfig(activeTenantId, {
      trainingCenter: { emailTemplates: { [templateId]: config } },
    });
    setEmailTemplates((prev) => ({ ...prev, [templateId]: config }));
  };

  const handleTestTemplate = async (templateId, toEmail) => {
    await sendTestEmailTemplate(activeTenantId, templateId, toEmail);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 2, sm: 4 } }}>
      <Typography sx={{ fontSize: 26, fontWeight: 800, letterSpacing: "-.02em", color: DS.ink, fontFamily: "var(--font-display)" }}>
        Email Library
      </Typography>
      <Typography sx={{ fontSize: 14, color: DS.ink3, mt: "6px", mb: "28px" }}>
        Every email starts from a built-in default. Customize the subject or intro of any template for
        this center, or reset it back to the default. Security emails are locked.
      </Typography>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: tPrimary }} />
        </Box>
      )}

      {loadError && (
        <Typography sx={{ color: DS.danger, fontSize: 14 }}>{loadError}</Typography>
      )}

      {!loading && !loadError && [...grouped.entries()].map(([family, familyEntries]) => (
        <Box key={family} sx={{ mb: "32px" }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: DS.ink3, mb: "12px" }}>
            {family}
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: "14px" }}>
            {familyEntries.map((entry) => {
              const def = TEMPLATE_DEFS[entry.templateId];
              const classification = CLASSIFICATION_COLORS[entry.classification] || CLASSIFICATION_COLORS.operational;
              return (
                <Box
                  key={entry.templateId}
                  sx={{
                    bgcolor: "#fff",
                    border: `1px solid ${DS.line}`,
                    borderRadius: "14px",
                    boxShadow: DS.shadowSm,
                    p: "18px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: "9px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: alpha(tPrimary, 0.08), color: tPrimary }}>
                      <MailOutlineIcon sx={{ fontSize: 17 }} />
                    </Box>
                    <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: DS.ink, lineHeight: 1.35 }}>
                      {def?.label || humanize(entry.templateId)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 12.5, color: DS.ink3, lineHeight: 1.5, flex: 1, minHeight: 32 }}>
                    {def?.description || ""}
                  </Typography>
                  <Chip
                    label={entry.classification}
                    size="small"
                    sx={{
                      alignSelf: "flex-start",
                      fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em",
                      bgcolor: classification.bg, color: classification.color,
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon sx={{ fontSize: 15 }} />}
                    onClick={() => handleOpenEditor(entry.templateId)}
                    data-testid={`email-template-open-${entry.templateId}`}
                    disabled={!entry.overridable}
                    sx={{
                      mt: "4px", alignSelf: "flex-start", textTransform: "none", fontWeight: 600, fontSize: 12.5,
                      borderRadius: "9px", border: `1.5px solid ${DS.line}`, color: DS.ink2,
                      "&:hover": { bgcolor: DS.surface2, borderColor: "var(--color-hairline-cool)" },
                    }}
                  >
                    {entry.overridable ? "Customize & Test" : "Locked"}
                  </Button>
                  {!entry.overridable && (
                    <Typography sx={{ fontSize: 11.5, color: DS.ink4 }}>
                      Security email — can&apos;t be customized.
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}

      <EmailTemplateEditorDialog
        open={editorOpen}
        templateId={editorTemplateId}
        entry={editorEntry}
        savedConfig={editorTemplateId ? (emailTemplates[editorTemplateId] ?? null) : null}
        tenantId={activeTenantId}
        userEmail={user?.email}
        onClose={() => setEditorOpen(false)}
        onSave={handleSaveTemplate}
        onTestSend={handleTestTemplate}
      />
    </Box>
  );
}
